/**
 * Phase 9.1 — OrderDetailModal.
 *
 * Fullscreen-ish dialog for `GET /api/orders/{order_id}/drill-down`.
 * SKU cells inside open ProductDetailModal on top, and canonical_name links
 * navigate to /employees/{id} (closing this modal via provider).
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { api } from "@/api/client";
import { useDrillDown } from "./DrillDownContext";
import { ClickableEmployee, ClickableSKU } from "./Clickables";

const fmt = (n) => (n ?? 0).toLocaleString("es-MX");
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "—");

export default function OrderDetailModal() {
  const { orderId, closeOrderModal, openProductModal } = useDrillDown();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setData(null);
      setErr(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/orders/${encodeURIComponent(orderId)}/drill-down`)
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (!orderId) return null;
  const k = data?.kpis;

  return (
    <Dialog open={!!orderId} onOpenChange={(v) => !v && closeOrderModal()}>
      <DialogContent
        data-testid="order-detail-modal"
        className="max-w-5xl gap-0 p-0 overflow-hidden max-h-[92vh]"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b bg-muted/40 px-6 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Folio / Orden
            </div>
            <div className="mt-0.5 font-mono text-lg font-bold" data-testid="order-id-header">
              {orderId}
            </div>
            {k?.date_range?.from && (
              <div className="mt-1 text-xs text-muted-foreground">
                {fmtDate(k.date_range.from)} → {fmtDate(k.date_range.to)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={closeOrderModal}
            className="rounded p-1 opacity-70 hover:bg-muted hover:opacity-100"
            data-testid="order-modal-close"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[75vh] overflow-y-auto px-6 py-4 space-y-5">
          {loading && <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>}
          {err && (
            <div className="rounded-md border border-red-800/40 bg-red-950/30 px-4 py-3 text-sm text-red-200">
              {err}
            </div>
          )}
          {!loading && data && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Kpi label="Movimientos" value={fmt(k.total_movements)} />
                <Kpi label="SKUs únicos" value={fmt(k.unique_skus)} />
                <Kpi label="Usuarios" value={fmt(k.unique_users)} />
                <Kpi
                  label="Rango"
                  value={
                    k.date_range?.from
                      ? `${fmtDate(k.date_range.from)} → ${fmtDate(k.date_range.to)}`
                      : "—"
                  }
                />
              </div>

              <Section title="SKUs resumen" testid="order-skus-summary">
                {data.skus_summary.length === 0 ? (
                  <p className="py-4 text-sm text-muted-foreground">—</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="pb-2">SKU</th>
                        <th className="pb-2">Descripción</th>
                        <th className="pb-2">Vendor</th>
                        <th className="pb-2">Tipo</th>
                        <th className="pb-2 text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {data.skus_summary.map((s, i) => (
                        <tr key={s.sku + i} className="hover:bg-muted/40">
                          <td className="py-1.5 font-mono">
                            <ClickableSKU sku={s.sku} onOpen={openProductModal}>
                              {s.sku}
                            </ClickableSKU>
                          </td>
                          <td className="max-w-[280px] truncate py-1.5 text-muted-foreground">
                            {s.description || "—"}
                          </td>
                          <td className="py-1.5 text-xs text-muted-foreground">
                            {s.vendor || "—"}
                          </td>
                          <td className="py-1.5">
                            <Badge variant="secondary" className="font-mono text-[10px]">
                              {s.canonical_type || "—"}
                            </Badge>
                          </td>
                          <td className="py-1.5 text-right tabular-nums">{fmt(s.qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>

              <Section title="Usuarios en esta orden" testid="order-users-summary">
                {data.users_summary.length === 0 ? (
                  <p className="py-2 text-sm text-muted-foreground">—</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.users_summary.map((u, i) => (
                      <li
                        key={u.canonical_id + i}
                        className="flex items-center justify-between rounded px-2 py-1 hover:bg-muted/40"
                      >
                        <ClickableEmployee canonicalId={u.canonical_id}>
                          {u.canonical_name || u.canonical_id}
                        </ClickableEmployee>
                        <span className="tabular-nums text-muted-foreground">{fmt(u.count)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value }) {
  return (
    <Card className="border-border/60 bg-muted/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums truncate">{value}</div>
    </Card>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}
